import React, { useState } from 'react';
import axios from 'axios';
import { X, Upload, Calendar, Film, Sparkles, Trophy, TrendingUp, ExternalLink, Building2 } from 'lucide-react';
import AdminFranchisesPanel from './AdminFranchisesPanel';
import PopCultureAutoPanel from './PopCultureAutoPanel';
import { toast } from 'sonner';

const AddCinemaContentModal = ({ onClose, onSuccess }) => {
  const [contentType, setContentType] = useState('film');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [director, setDirector] = useState('');
  const [genre, setGenre] = useState('');
  const [trailerUrl, setTrailerUrl] = useState('');
  const [year, setYear] = useState('');
  const [category, setCategory] = useState('pop_culture');
  const [filmSerieType, setFilmSerieType] = useState('film');
  const [rank, setRank] = useState(1);
  const [externalUrl, setExternalUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 5 MB');
        return;
      }
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      
      let endpoint = '';
      
      switch (contentType) {
        case 'film':
          endpoint = '/api/admin/films';
          formData.append('description', description);
          formData.append('release_date', releaseDate);
          formData.append('director', director);
          formData.append('genre', genre);
          formData.append('trailer_url', trailerUrl);
          if (imageFile) formData.append('poster', imageFile);
          break;
          
        case 'pop_culture':
          endpoint = '/api/admin/pop-culture';
          formData.append('description', description);
          formData.append('category', category);
          formData.append('external_url', externalUrl);
          if (imageFile) formData.append('image', imageFile);
          break;
          
        case 'best_film':
          endpoint = '/api/admin/best-films';
          formData.append('year', year);
          formData.append('content_type', filmSerieType);
          formData.append('external_url', externalUrl);
          if (imageFile) formData.append('image', imageFile);
          break;
          
        case 'box_office':
          endpoint = '/api/admin/box-office';
          formData.append('rank', rank);
          if (imageFile) formData.append('image', imageFile);
          break;
          
        default:
          toast.error('Type de contenu non reconnu');
          setUploading(false);
          return;
      }

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000 // 30 seconds timeout
      });

      if (response.data && response.data.id) {
        toast.success('Contenu ajouté avec succès!');
        onSuccess();
      } else {
        toast.success('Contenu ajouté!');
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding content:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de l\'ajout';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const contentTypes = [
    { id: 'film', label: 'Sortie Cinéma', icon: Film, color: 'yellow' },
    { id: 'pop_culture', label: 'Pop Culture / Manga / Théâtre', icon: Sparkles, color: 'pink' },
    { id: 'best_film', label: 'Meilleur Film/Série', icon: Trophy, color: 'amber' },
    { id: 'box_office', label: 'Box Office', icon: TrendingUp, color: 'green' },
    { id: 'fss', label: 'Franchises / Studios / Streaming', icon: Building2, color: 'purple' },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Ajouter du Contenu Cinéma</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content Type Selector */}
        <div className="p-4 border-b border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {contentTypes.map(type => {
              const Icon = type.icon;
              const isActive = contentType === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setContentType(type.id)}
                  data-testid={`add-content-type-${type.id}`}
                  className={`p-3 rounded-xl text-center transition-all ${
                    isActive 
                      ? `bg-${type.color}-500/20 border border-${type.color}-500 text-${type.color}-400` 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${isActive ? `text-${type.color}-400` : ''}`} />
                  <span className="text-xs font-medium">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        {contentType === 'fss' ? (
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <AdminFranchisesPanel />
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[55vh] space-y-4">
          {/* Title - Common to all */}
          <div>
            <label className="block text-sm font-semibold mb-2">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du contenu..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
              required
            />
          </div>

          {/* Fields for Film */}
          {contentType === 'film' && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-2">Synopsis</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Synopsis du film..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Date de sortie *</label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Réalisateur</label>
                  <input
                    type="text"
                    value={director}
                    onChange={(e) => setDirector(e.target.value)}
                    placeholder="James Cameron"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Genre</label>
                  <select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Action">Action</option>
                    <option value="Comédie">Comédie</option>
                    <option value="Drame">Drame</option>
                    <option value="Horreur">Horreur</option>
                    <option value="Science-Fiction">Science-Fiction</option>
                    <option value="Thriller">Thriller</option>
                    <option value="Romance">Romance</option>
                    <option value="Animation">Animation</option>
                    <option value="Documentaire">Documentaire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Lien YouTube</label>
                  <input
                    type="url"
                    value={trailerUrl}
                    onChange={(e) => setTrailerUrl(e.target.value)}
                    placeholder="https://youtube.com/..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Fields for Pop Culture */}
          {contentType === 'pop_culture' && (
            <>
              <PopCultureAutoPanel />
              <div>
                <label className="block text-sm font-semibold mb-2">Catégorie</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500"
                >
                  <option value="pop_culture">Pop Culture</option>
                  <option value="manga">Manga</option>
                  <option value="theatre">Théâtre</option>
                  <option value="world_cultures">Cultures & civilisations</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Description (max 5 lignes)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description courte de l'actualité..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{description.length}/500 caractères</p>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  <div className="flex items-center space-x-2">
                    <ExternalLink className="w-4 h-4 text-pink-400" />
                    <span>Lien externe (En savoir plus)</span>
                  </div>
                </label>
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://exemple.com/article..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">Ce lien s'affichera sous la description dans "Découvrir"</p>
              </div>
            </>
          )}

          {/* Fields for Best Films */}
          {contentType === 'best_film' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Année</label>
                  <input
                    type="text"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="2025"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Type</label>
                  <select
                    value={filmSerieType}
                    onChange={(e) => setFilmSerieType(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500"
                  >
                    <option value="film">Film</option>
                    <option value="serie">Série</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  <div className="flex items-center space-x-2">
                    <ExternalLink className="w-4 h-4 text-amber-400" />
                    <span>Lien externe (En savoir plus)</span>
                  </div>
                </label>
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://imdb.com/title/..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">Ce lien s'affichera dans "Découvrir"</p>
              </div>
            </>
          )}

          {/* Fields for Box Office */}
          {contentType === 'box_office' && (
            <div>
              <label className="block text-sm font-semibold mb-2">Rang au Box Office</label>
              <input
                type="number"
                min="1"
                value={rank}
                onChange={(e) => setRank(parseInt(e.target.value) || 1)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500"
              />
            </div>
          )}

          {/* Image Upload - Common to all */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              {contentType === 'film' ? 'Affiche du film' : 'Image'}
            </label>
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-magenta-500/50 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="flex flex-col items-center space-y-2">
                  <Upload className="w-10 h-10 text-gray-400" />
                  <div>
                    <p className="font-medium text-sm">
                      {imageFile ? imageFile.name : 'Cliquer pour télécharger'}
                    </p>
                    <p className="text-xs text-gray-400">jpg, png jusqu'à 5 MB</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-full font-semibold transition-colors disabled:opacity-50"
            >
              {uploading ? 'Ajout en cours...' : 'Ajouter'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AddCinemaContentModal;
